import { SEC_ADV_FIRMS_PATH } from './admin-page';
import { CardLink } from './participant-link';

/**
 * A register firm's legal name as the way to its card (`/admin/sec-adv/firms/:firmCrd`,
 * survey-api's SR-214) — one component for every screen that names a firm: the firm list, and
 * the SEC ADV register firms under the person hub's registry answers.
 *
 * `ParticipantLink`'s shape, for its reason: **the link carries the search it came from.** The
 * firm list keeps its whole query in the URL, and a card opened from it sends that query along in
 * the history entry's state, so the card's way back returns to the search rather than to the
 * unfiltered list. It is the participant link's `CardLink`, state and all, so the firm card reads
 * it back with the same `searchFrom`. Only the list has a search to send; any other link sends
 * none. Browser history restores the search on its own either way — this is for the link on the
 * page.
 *
 * Addressed by **firm CRD**, as survey-api addresses a firm: digits only and permanent, where the
 * SEC number changes when an exempt reporting adviser registers. Encoded anyway, since a path
 * segment is built from it and the card's route takes whatever the address holds.
 */

export function firmCardPath(firmCrd: string): string {
  return `${SEC_ADV_FIRMS_PATH}/${encodeURIComponent(firmCrd)}`;
}

/** A firm page's tabs (SR-423), Overview at the firm's own address. */
export type FirmTab = 'overview' | 'analytics' | 'filings' | 'affiliates' | 'internal';

export function firmTabPath(firmCrd: string, tab: FirmTab): string {
  return tab === 'overview' ? firmCardPath(firmCrd) : `${firmCardPath(firmCrd)}/${tab}`;
}

/** The firm's latest ADV Part 1 at one answer — `FormInfo.Part1A.Item5F.Q5F2C` — the
 *  click-through from a value to its source. */
export function firmSourcePath(firmCrd: string, source: string): string {
  return `${firmTabPath(firmCrd, 'filings')}?${new URLSearchParams({ item: source })}`;
}

export function FirmLink({
  firm,
  search,
  fromList,
  className
}: {
  firm: { firmCrd: string; legalName: string };
  /** The list's own query, when the link is on the list. */
  search?: string;
  /** The link is on the list, whose card is then drawn beside it. */
  fromList?: boolean;
  className?: string;
}) {
  return (
    <CardLink
      to={firmCardPath(firm.firmCrd)}
      search={search}
      fromList={fromList}
      className={className}
    >
      {firm.legalName}
    </CardLink>
  );
}
